<template>
  <base-dialog :active="themeDialogVisible" :title="t('toolbar.Theme settings')" :cancel-text="t('common.Close')"
    @close="themeDialogVisible = false" @cancel="themeDialogVisible = false">
    <div class="p-4 space-y-6">
      <div class="flex flex-col space-y-4">
        <div
          class="flex items-center p-4 rounded-lg cursor-pointer transition-colors duration-200 hover:bg-stone-100 dark:hover:bg-stone-700"
          :class="{ 'bg-stone-100 dark:bg-stone-700': themePreference === 'system' }"
          @click="setThemePreference('system')">
          <div class="flex items-center justify-center h-12 w-12 text-primary-500">
            <i class="fas fa-desktop text-2xl"></i>
          </div>
          <div class="ml-4">
            <div class="font-bold text-lg text-stone-800 dark:text-stone-200">{{ t('toolbar.System theme') }}</div>
            <div v-if="themePreference === 'system'" class="text-sm text-stone-600 dark:text-stone-400 mt-1">
              {{ t('toolbar.Currently using') }}: {{ isDarkMode ? t('toolbar.Dark') : t('toolbar.Light') }}
            </div>
          </div>
        </div>

        <div
          class="flex items-center p-4 rounded-lg cursor-pointer transition-colors duration-200 hover:bg-stone-100 dark:hover:bg-stone-700"
          :class="{ 'bg-stone-100 dark:bg-stone-700': themePreference === 'light' }"
          @click="setThemePreference('light')">
          <div class="flex items-center justify-center h-12 w-12 text-primary-500">
            <i class="fas fa-sun text-2xl"></i>
          </div>
          <div class="ml-4">
            <div class="font-bold text-lg text-stone-800 dark:text-stone-200">{{ t('toolbar.Light theme') }}</div>
          </div>
        </div>

        <div
          class="flex items-center p-4 rounded-lg cursor-pointer transition-colors duration-200 hover:bg-stone-100 dark:hover:bg-stone-700"
          :class="{ 'bg-stone-100 dark:bg-stone-700': themePreference === 'dark' }" @click="setThemePreference('dark')">
          <div class="flex items-center justify-center h-12 w-12 text-primary-500">
            <i class="fas fa-moon text-2xl"></i>
          </div>
          <div class="ml-4">
            <div class="font-bold text-lg text-stone-800 dark:text-stone-200">{{ t('toolbar.Dark theme') }}</div>
          </div>
        </div>
      </div>
    </div>
  </base-dialog>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePreferencesStore } from '@/stores/usePreferencesStore';
import * as Util from '@utils/Util.js';
import BaseDialog from '../shared/BaseDialog.vue';
import { onOpen as onDialogOpen } from '@/services/ui/DialogCoordinator.js';

const { t } = useI18n();
const preferencesStore = usePreferencesStore();

const themeDialogVisible = ref(false);
const themePreference = computed(() => preferencesStore.themePreference);
const isDarkMode = computed(() => preferencesStore.darkMode);

const show = () => {
  themeDialogVisible.value = true;
};

// Close if another dialog opens
let unsubscribeDialogOpen = null;
onMounted(() => {
  unsubscribeDialogOpen = onDialogOpen((name) => {
    if (name !== 'theme') {
      themeDialogVisible.value = false;
    }
  });
});
onBeforeUnmount(() => {
  if (typeof unsubscribeDialogOpen === 'function') {
    try { unsubscribeDialogOpen(); } catch { }
  }
});

const setThemePreference = (preference) => {
  Util.trackEvent('Wheel', 'SetThemePreference', preference);
  preferencesStore.setThemePreference(preference);
};

defineExpose({ show });
</script>

<style scoped>
.theme-options {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.theme-option {
  display: flex;
  align-items: center;
  padding: 1rem;
  border-radius: var(--md-shape-corner-small);
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;
}

.theme-option:hover {
  background-color: var(--md-surface-1, rgba(0, 0, 0, 0.05));
}

.theme-option.active {
  background-color: var(--md-surface-2, rgba(0, 0, 0, 0.08));
}

.option-details {
  margin-left: 1rem;
}

.option-title {
  font-weight: bold;
  font-size: 1.1rem;
  color: var(--md-on-surface);
}

.option-description {
  font-size: 0.9rem;
  color: var(--md-on-surface-variant, #777);
  margin-top: 0.25rem;
}

.icon.is-large {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 3rem;
  width: 3rem;
  color: var(--md-primary);
}

/* Override button styling to ensure theme consistency */
.modal-card-foot .button {
  margin-left: 0.5rem;
}
</style>
